// src/features/services/sales/api/stockApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Stock — FE API layer.
//
// Source of truth: scope-entity system (namespace "stock.balance" / "stock.movement").
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/stock';

// ── Types ────────────────────────────────────────────────────────────────────

export interface StockBalance {
  balance_key: string;
  id_scope: number | null;
  sku: string;
  quantity: number;        // float-capable
  unit: string;
  last_movement_at: string | null;
  last_movement_type: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface StockMovement {
  movement_key: string;
  id_scope: number | null;
  sku: string;
  movement_type: string;   // wo_yield | wo_consume | adjustment | reservation_commit
  quantity_delta: number;
  balance_after: number;
  reference_type: string | null;
  reference_key: string | null;
  notes: string;
  created_by: string | null;
  created_at: string | null;
  lot_number: string | null;
  expiry_date: string | null;
}

export interface StockListResponse {
  items: StockBalance[];
  next_cursor: string | null;
}

export interface AdjustStockPayload {
  quantity_delta: number;
  notes?: string;
  unit?: string;
  lot_number?: string;
  expiry_date?: string;
}

export interface AdjustStockResponse {
  balance: StockBalance;
  movement: {
    movement_key: string;
    quantity_delta: number;
    balance_after: number;
  };
}

export interface MovementListResponse {
  items: StockMovement[];
  next_cursor: string | null;
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const STOCK_KEYS = {
  all:          ['stock'] as const,
  list:         (params?: Record<string, unknown>) => ['stock', 'list', params] as const,
  detail:       (sku: string) => ['stock', 'detail', sku] as const,
  movements:    (sku: string) => ['stock', 'movements', sku] as const,
  lowStock:     ['stock', 'low-stock'] as const,
  availability: (sku: string) => ['stock', 'availability', sku] as const,
  reservations: (sku: string) => ['stock', 'reservations', sku] as const,
  lots:         (sku: string) => ['stock', 'lots', sku] as const,
};

// ── API functions ────────────────────────────────────────────────────────────

export async function fetchStockListApi(
  params?: { limit?: number; cursor?: string },
): Promise<StockListResponse> {
  return bff.get<StockListResponse>(BASE, params as any);
}

export async function fetchStockBalanceApi(sku: string): Promise<StockBalance> {
  return bff.get<StockBalance>(`${BASE}/${encodeURIComponent(sku)}`);
}

export async function adjustStockApi(
  sku: string,
  payload: AdjustStockPayload,
): Promise<AdjustStockResponse> {
  return bff.post<AdjustStockResponse, AdjustStockPayload>(
    `${BASE}/${encodeURIComponent(sku)}/adjust`,
    payload,
  );
}

export async function fetchMovementsApi(
  sku: string,
  params?: { limit?: number; cursor?: string },
): Promise<MovementListResponse> {
  return bff.get<MovementListResponse>(
    `${BASE}/${encodeURIComponent(sku)}/movements`,
    params as any,
  );
}

// ── P3.1 / P3.2 — Low-stock alerts ──────────────────────────────────────────

export interface LowStockAlert {
  sku: string;
  on_hand: number;
  reserved: number;
  available: number;
  reorder_point: number;
  deficit: number;
  suggested_order: number;
  unit: string;
}

export interface LowStockResponse {
  items: LowStockAlert[];
}

export async function fetchLowStockAlertsApi(): Promise<LowStockResponse> {
  return bff.get<LowStockResponse>(`${BASE}/low-stock`);
}

// ── P3.3 — Stock availability ────────────────────────────────────────────────

export interface StockAvailability {
  sku: string;
  on_hand: number;
  reserved: number;
  available: number;
  unit: string;
}

export async function fetchAvailabilityApi(sku: string): Promise<StockAvailability> {
  return bff.get<StockAvailability>(`${BASE}/${encodeURIComponent(sku)}/available`);
}

// ── P3.3 — Reservations ─────────────────────────────────────────────────────

export interface StockReservation {
  reservation_key: string;
  id_scope: number | null;
  sku: string;
  quantity: number;
  unit: string;
  reference_type: string | null;
  reference_key: string | null;
  status: 'pending' | 'committed' | 'released';
  reserved_at: string | null;
  reserved_by: string | null;
  committed_at: string | null;
  released_at: string | null;
  notes: string;
}

export interface ReservationListResponse {
  items: StockReservation[];
  next_cursor: string | null;
}

export interface ReserveStockPayload {
  quantity: number;
  reference_key: string;
  reference_type?: string;
  unit?: string;
  notes?: string;
}

export interface ReserveStockResponse {
  reservation: StockReservation;
}

export async function reserveStockApi(
  sku: string,
  payload: ReserveStockPayload,
): Promise<ReserveStockResponse> {
  return bff.post<ReserveStockResponse, ReserveStockPayload>(
    `${BASE}/${encodeURIComponent(sku)}/reserve`,
    payload,
  );
}

export async function fetchReservationsApi(
  sku: string,
  params?: { status?: string },
): Promise<ReservationListResponse> {
  return bff.get<ReservationListResponse>(
    `${BASE}/${encodeURIComponent(sku)}/reservations`,
    params as any,
  );
}

export async function commitReservationApi(key: string): Promise<void> {
  return bff.post<void, Record<string, never>>(
    `${BASE}/reservations/${encodeURIComponent(key)}/commit`,
    {},
  );
}

export async function releaseReservationApi(key: string): Promise<void> {
  return bff.post<void, Record<string, never>>(
    `${BASE}/reservations/${encodeURIComponent(key)}/release`,
    {},
  );
}

// ── P3.4 — Lot/Batch tracking ───────────────────────────────────────────────

export interface StockLot {
  lot_number: string;
  net_qty: number;
  earliest_expiry: string | null;
  movement_count: number;
  unit: string;
}

export interface LotListResponse {
  items: StockLot[];
}

export async function fetchLotsApi(sku: string): Promise<LotListResponse> {
  return bff.get<LotListResponse>(`${BASE}/${encodeURIComponent(sku)}/lots`);
}
